import React from "react";
import { Link } from "react-router-dom";
import { Footer } from "../../components/Footer";
import { SEOHead } from "../../components/SEOHead";

/**
 * About page for etlyn.com — public, accessible without login.
 * Business-focused: positions Etlyn as a software agency that helps
 * companies grow customers, increase sales and manage content at scale.
 * Built with Tailwind CSS only (no Mantine).
 */

/* ────────────────────────────────────────────────────────── */
/*  Section data                                              */
/* ────────────────────────────────────────────────────────── */

const results = [
  { metric: "3×", label: "Faster time to market with our CMS platform" },
  { metric: "40%", label: "Average increase in online conversion rates" },
  { metric: "99.9%", label: "Uptime across production deployments" },
  { metric: "24h", label: "Content updates — no developer needed" },
];

const whatWeBuild = [
  {
    title: "Custom Web Applications",
    description:
      "High-performance web apps built to convert visitors into customers. SEO-optimised, lightning-fast, and designed around your sales funnel.",
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 21a9.004 9.004 0 0 0 8.716-6.747M12 21a9.004 9.004 0 0 1-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 0 1 7.843 4.582M12 3a8.997 8.997 0 0 0-7.843 4.582m15.686 0A11.953 11.953 0 0 1 12 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0 1 21 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0 1 12 16.5a17.92 17.92 0 0 1-8.716-2.247m0 0A8.966 8.966 0 0 1 3 12c0-1.264.26-2.467.732-3.559" />
      </svg>
    ),
    blob: "from-[#EEF1FB]/80",
  },
  {
    title: "Mobile Apps (iOS & Android)",
    description:
      "Native-quality mobile apps that put your business in your customers' pockets — with push notifications, offline support and seamless payments.",
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 1.5H8.25A2.25 2.25 0 0 0 6 3.75v16.5a2.25 2.25 0 0 0 2.25 2.25h7.5A2.25 2.25 0 0 0 18 20.25V3.75a2.25 2.25 0 0 0-2.25-2.25H13.5m-3 0V3h3V1.5m-3 0h3m-3 18.75h3" />
      </svg>
    ),
    blob: "from-[#F3EEFF]/80",
  },
  {
    title: "CMS & Content Platforms",
    description:
      "Our built-in CMS lets your marketing team publish blogs, landing pages and campaigns in minutes — no developer bottleneck, no delays to market.",
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" />
      </svg>
    ),
    blob: "from-[#EFF9FF]/80",
  },
  {
    title: "E-Commerce & SaaS",
    description:
      "End-to-end platforms built to sell — from product catalogues and subscription billing to analytics dashboards that reveal exactly where revenue grows.",
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 18.75a60.07 60.07 0 0 1 15.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 0 1 3 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25 6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-.375m1.5-1.5H21a.75.75 0 0 0-.75.75v.75m0 0H3.75m0 0h-.375a1.125 1.125 0 0 1-1.125-1.125V15m1.5 1.5v-.75A.75.75 0 0 0 3 15h-.75M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Zm3 0h.008v.008H18V10.5Zm-12 0h.008v.008H6V10.5Z" />
      </svg>
    ),
    blob: "from-[#EAFCEE]/80",
  },
];

const whyEtlyn = [
  {
    title: "Revenue-Focused Engineering",
    items: [
      "Every feature tied to a measurable business outcome",
      "Conversion-optimised UI patterns backed by A/B testing",
      "Analytics built in from day one — not bolted on later",
      "Fast load times that reduce bounce and increase sales",
    ],
  },
  {
    title: "Built-In CMS — No Extra Cost",
    items: [
      "Publish blogs, pages and campaigns without developers",
      "Real-time content preview so you ship with confidence",
      "SEO metadata, open-graph tags and sitemap generation included",
      "Multi-language support (i18n) for international growth",
    ],
  },
  {
    title: "Scale Without Rebuilding",
    items: [
      "Multi-tenant architecture grows from 1 to 10,000 clients",
      "Cloud-native deployment with auto-scaling infrastructure",
      "API-first backend — integrate with any CRM, ERP or tool",
      "Modular codebase lets you add features, not rewrite them",
    ],
  },
  {
    title: "Your Team Stays in Control",
    items: [
      "Full source-code ownership — no vendor lock-in, ever",
      "Role-based access so editors, marketers and devs all work safely",
      "Comprehensive documentation and onboarding support",
      "Ongoing maintenance plans to keep your product competitive",
    ],
  },
];

const cmsCapabilities = [
  {
    title: "Flexible Content Architecture",
    description:
      "Our CMS separates your content from presentation. Your marketing team manages pages, blogs and media while customers get blazing-fast experiences.",
    icon: (
      <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M20.25 6.375c0 2.278-3.694 4.125-8.25 4.125S3.75 8.653 3.75 6.375m16.5 0c0-2.278-3.694-4.125-8.25-4.125S3.75 4.097 3.75 6.375m16.5 0v11.25c0 2.278-3.694 4.125-8.25 4.125s-8.25-1.847-8.25-4.125V6.375m16.5 0v3.75m-16.5-3.75v3.75m16.5 0v3.75C20.25 16.153 16.556 18 12 18s-8.25-1.847-8.25-4.125v-3.75m16.5 0c0 2.278-3.694 4.125-8.25 4.125s-8.25-1.847-8.25-4.125" />
      </svg>
    ),
  },
  {
    title: "Publish in Minutes, Not Days",
    description:
      "Your team can launch landing pages, blog posts and promotions the moment they're ready — no code changes, no deploy queues, no waiting.",
    icon: (
      <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
      </svg>
    ),
  },
  {
    title: "SEO & Growth Built In",
    description:
      "Automatic sitemaps, structured data, meta-tag management and open-graph previews mean every piece of content is optimised to attract and convert.",
    icon: (
      <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 3v11.25A2.25 2.25 0 0 0 6 16.5h2.25M3.75 3h-1.5m1.5 0h16.5m0 0h1.5m-1.5 0v11.25A2.25 2.25 0 0 1 18 16.5h-2.25m-7.5 0h7.5m-7.5 0-1 3m8.5-3 1 3m0 0 .5 1.5m-.5-1.5h-9.5m0 0-.5 1.5M9 11.25v1.5M12 9v3.75m3-6v6" />
      </svg>
    ),
  },
  {
    title: "Connect Everything via API",
    description:
      "A fully documented API lets you connect your CMS to CRMs, email tools, analytics services and any custom workflow your business needs.",
    icon: (
      <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M17.25 6.75 22.5 12l-5.25 5.25m-10.5 0L1.5 12l5.25-5.25m7.5-3-4.5 16.5" />
      </svg>
    ),
  },
];

const techStack = [
  { label: "Web Applications" },
  { label: "Mobile Development" },
  { label: "Cloud Infrastructure" },
  { label: "Content Management" },
  { label: "API Development" },
  { label: "Data & Analytics" },
];

/* ────────────────────────────────────────────────────────── */
/*  Component                                                 */
/* ────────────────────────────────────────────────────────── */

export default function About() {
  return (
    <div className="w-full max-w-[1280px] mx-auto px-4 overflow-x-hidden mt-[80px] font-['Inter',sans-serif]">
      <SEOHead
        title="About Etlyn — Software Development Agency"
        description="Etlyn is a software agency that turns business goals into high-performance web apps, mobile products and CMS platforms. Learn about our team, process and results."
        path="/about"
        keywords="software agency, about Etlyn, development team, agile software development, business technology partner"
      />

      {/* ─── Hero — clean, text-led ──────────────────────── */}
      <section className="pt-8 pb-14 sm:pt-12 sm:pb-20 text-center mb-4">
        <span className="inline-block text-xs font-semibold uppercase tracking-[0.15em] text-[#0D2B7E] mb-4">
          Software Development Agency
        </span>
        <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#333D51] leading-tight tracking-tight mb-5 max-w-3xl mx-auto">
          We build software that grows your customers and&nbsp;increases&nbsp;your&nbsp;sales
        </h1>
        <p className="text-sm sm:text-base text-[#666D7C] leading-relaxed max-w-2xl mx-auto mb-8">
          Etlyn is a software agency that turns business goals into
          high-performance web apps, mobile products and CMS platforms.
          We don't just write code — we engineer growth.
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
          <Link
            to="/home#contact"
            className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-r from-[#0D2B7E] to-[#25408B] px-6 py-3 text-sm font-semibold text-white shadow-md transition hover:-translate-y-0.5 hover:shadow-lg"
          >
            Start a Project
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
            </svg>
          </Link>
          <a
            href="#what-we-build"
            className="inline-flex items-center gap-1.5 rounded-lg border border-[#DFDFE0] px-6 py-3 text-sm font-medium text-[#333D51] transition hover:border-[#0D2B7E] hover:text-[#0D2B7E]"
          >
            See What We Build
          </a>
        </div>
      </section>

      {/* ─── Social Proof Numbers ────────────────────────── */}
      <section className="mb-20 grid grid-cols-2 md:grid-cols-4 gap-4">
        {results.map((r) => (
          <div
            key={r.label}
            className="relative overflow-hidden rounded-2xl bg-white p-5 ring-1 ring-[#DFDFE0]/60 text-center"
          >
            <div className="pointer-events-none absolute -right-6 -top-6 h-28 w-28 rounded-full bg-gradient-to-bl from-[#EEF1FB]/80 to-transparent" />
            <div className="relative">
              <span className="block text-2xl sm:text-3xl font-bold text-[#0D2B7E] tracking-tight">
                {r.metric}
              </span>
              <span className="block mt-1 text-xs sm:text-sm text-[#666D7C] leading-snug">
                {r.label}
              </span>
            </div>
          </div>
        ))}
      </section>

      {/* ─── What We Build ───────────────────────────────── */}
      <section id="what-we-build" className="mb-20 scroll-mt-24">
        <div className="text-center mb-12">
          <h2 className="text-xl font-semibold tracking-tight text-[#333D51]">
            What We Build
          </h2>
          <p className="text-sm text-[#808692] mt-1 max-w-lg mx-auto">
            End-to-end digital products designed to acquire customers,
            drive revenue and simplify operations.
          </p>
        </div>

        <div className="grid gap-6 sm:grid-cols-2">
          {whatWeBuild.map((item) => (
            <div
              key={item.title}
              className="relative overflow-hidden rounded-2xl bg-white p-6 ring-1 ring-[#DFDFE0]/60 transition-shadow hover:shadow-md"
            >
              <div
                className={`pointer-events-none absolute -right-6 -top-6 h-28 w-28 rounded-full bg-gradient-to-bl ${item.blob} to-transparent`}
              />
              <div className="relative flex gap-4">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#EEF1FB] text-[#0D2B7E]">
                  {item.icon}
                </div>
                <div>
                  <h3 className="text-base font-semibold text-[#333D51] mb-1">
                    {item.title}
                  </h3>
                  <p className="text-sm text-[#666D7C] leading-relaxed">
                    {item.description}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ─── Why Etlyn ───────────────────────────────────── */}
      <section className="mb-20">
        <div className="text-center mb-12">
          <h2 className="text-xl font-semibold tracking-tight text-[#333D51]">
            Why Businesses Choose Etlyn
          </h2>
          <p className="text-sm text-[#808692] mt-1 max-w-lg mx-auto">
            We measure success by your growth metrics — not lines of code.
          </p>
        </div>

        <div className="grid gap-6 sm:grid-cols-2">
          {whyEtlyn.map((card) => (
            <div
              key={card.title}
              className="rounded-2xl bg-white p-6 ring-1 ring-[#DFDFE0]/60"
            >
              <h3 className="text-base font-semibold text-[#333D51] mb-3">
                {card.title}
              </h3>
              <ul className="space-y-2">
                {card.items.map((item, idx) => (
                  <li
                    key={idx}
                    className="flex items-start gap-2 text-sm text-[#666D7C] leading-relaxed"
                  >
                    <svg className="mt-0.5 h-4 w-4 shrink-0 text-[#28E454]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
                    </svg>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      {/* ─── CMS Capabilities ────────────────────────────── */}
      <section className="mb-20">
        <div className="text-center mb-12">
          <h2 className="text-xl font-semibold tracking-tight text-[#333D51]">
            Built-In Content Management
          </h2>
          <p className="text-sm text-[#808692] mt-1 max-w-lg mx-auto">
            Stop paying for a separate CMS. Every Etlyn project ships with a
            content management engine your team can use from day&nbsp;one.
          </p>
        </div>

        <div className="grid gap-6 sm:grid-cols-2">
          {cmsCapabilities.map((cap) => (
            <div
              key={cap.title}
              className="relative overflow-hidden rounded-2xl bg-white p-6 ring-1 ring-[#DFDFE0]/60 transition-shadow hover:shadow-md"
            >
              <div className="pointer-events-none absolute -right-6 -top-6 h-28 w-28 rounded-full bg-gradient-to-bl from-[#EFF9FF]/80 to-transparent" />
              <div className="relative">
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-[#0D2B7E] to-[#25408B] text-white mb-4">
                  {cap.icon}
                </div>
                <h3 className="text-base font-semibold text-[#333D51] mb-1">
                  {cap.title}
                </h3>
                <p className="text-sm text-[#666D7C] leading-relaxed">
                  {cap.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ─── Our Process ─────────────────────────────────── */}
      <section className="mb-20">
        <div className="text-center mb-12">
          <h2 className="text-xl font-semibold tracking-tight text-[#333D51]">
            From Idea to Revenue — In 4 Steps
          </h2>
          <p className="text-sm text-[#808692] mt-1 max-w-lg mx-auto">
            A proven, transparent process that keeps your budget predictable
            and your launch date on track.
          </p>
        </div>

        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {[
            {
              step: "01",
              title: "Discovery & Strategy",
              text: "We dig into your market, competitors and revenue goals to define the product that will move the needle for your business.",
            },
            {
              step: "02",
              title: "UX & Prototyping",
              text: "Conversion-focused wireframes and interactive prototypes — tested with real users before we write a single line of code.",
            },
            {
              step: "03",
              title: "Agile Development",
              text: "Two-week sprints with live demos. You see working software early and often, so there are no surprises at launch.",
            },
            {
              step: "04",
              title: "Launch & Growth",
              text: "We deploy, monitor performance, and iterate based on real analytics — optimising for the metrics that matter to your bottom line.",
            },
          ].map((s) => (
            <div
              key={s.step}
              className="relative overflow-hidden rounded-2xl bg-white p-6 ring-1 ring-[#DFDFE0]/60"
            >
              <span className="absolute -right-2 -top-4 text-[72px] font-extrabold leading-none text-[#EEF1FB] select-none">
                {s.step}
              </span>
              <div className="relative">
                <h3 className="text-base font-semibold text-[#333D51] mb-2">
                  {s.title}
                </h3>
                <p className="text-sm text-[#666D7C] leading-relaxed">
                  {s.text}
                </p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ─── Tech Stack (compact) ────────────────────────── */}
      <section className="mb-20">
        <div className="text-center mb-8">
          <h2 className="text-xl font-semibold tracking-tight text-[#333D51]">
            Our Core Capabilities
          </h2>
          <p className="text-sm text-[#808692] mt-1 max-w-lg mx-auto">
            Everything you need to build, launch and grow — under one roof.
          </p>
        </div>

        <div className="flex flex-wrap justify-center gap-3">
          {techStack.map((t) => (
            <span
              key={t.label}
              className="inline-flex items-center gap-1.5 rounded-full bg-[#EEF1FB] px-4 py-2 text-sm font-medium text-[#0D2B7E]"
            >
              <span className="h-1.5 w-1.5 rounded-full bg-[#0D2B7E]/40" />
              {t.label}
            </span>
          ))}
        </div>
      </section>

      {/* ─── CTA ─────────────────────────────────────────── */}
      <section className="mb-16 rounded-2xl bg-gradient-to-br from-[#0D2B7E] to-[#25408B] px-6 py-14 sm:px-12 text-center">
        <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3 tracking-tight">
          Let's grow your business together
        </h2>
        <p className="text-sm sm:text-base text-white/70 mb-8 max-w-xl mx-auto leading-relaxed">
          Tell us about your project and we'll show you how custom software
          and a built-in CMS can turn your website into your best&nbsp;salesperson.
        </p>
        <Link
          to="/home#contact"
          className="inline-flex items-center gap-2 rounded-lg bg-white px-6 py-3 text-sm font-semibold text-[#0D2B7E] shadow-md transition hover:-translate-y-0.5 hover:shadow-lg"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.243a2.25 2.25 0 0 1-1.07 1.916l-7.5 4.615a2.25 2.25 0 0 1-2.36 0L3.32 8.91a2.25 2.25 0 0 1-1.07-1.916V6.75" />
          </svg>
          Start a Conversation
        </Link>
      </section>

      {/* ─── Footer (reuse existing) ─────────────────────── */}
      <Footer isMob={window.innerWidth < 768} />
    </div>
  );
}
